import classNames from 'classnames';
import React, { useCallback, useEffect } from 'react';
import { Button } from '@mui/material';
import { RefState } from 'hooks/useRefState';
import { Player } from 'components/CamPlayerKbygRewinds/usePlayerState/usePlayerState';
import Play from './icons/Play';
import SkipBackward from './icons/SkipBackward';
import SkipForward from './icons/SkipForward';
import Pause from './icons/Pause';

import styles from './CamPlayerScrubberControls.module.scss';

export interface CamPlayerScrubberControlsProps {
  playing: boolean;
  setPlayerTime?: (utcTimeToPlay: number, wantsRewindPlaying: boolean, forcePlay?: boolean) => void;
  currentTimestampRef?: RefState<Date | undefined>;
  hasCurrentTimestamp: boolean;
  playerRef: HTMLElement | null;
  userWantsRewindPlaying: boolean;
  controlsVisible?: boolean;
  currentPreviewPlayer: Player;
  hasCamRewindPermissions: boolean;
  onTogglePlayPause: (event: Event | React.MouseEvent) => void;
}

const CamPlayerScrubberControls = ({
  playing,
  setPlayerTime,
  userWantsRewindPlaying,
  currentTimestampRef,
  hasCurrentTimestamp,
  playerRef,
  controlsVisible,
  currentPreviewPlayer,
  hasCamRewindPermissions,
  onTogglePlayPause,
}: CamPlayerScrubberControlsProps) => {
  const SKIP_TIME = 5000;

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.code === 'Space') {
        event.preventDefault();
        onTogglePlayPause(event);
      }
      const currentTimestamp = currentTimestampRef?.current?.value;
      if (event.code === 'ArrowLeft') {
        if (currentTimestamp && setPlayerTime) {
          setPlayerTime(currentTimestamp.getTime() - SKIP_TIME, userWantsRewindPlaying);
        }
      }
      if (event.code === 'ArrowRight') {
        if (currentTimestamp && setPlayerTime) {
          setPlayerTime(currentTimestamp.getTime() + SKIP_TIME, userWantsRewindPlaying);
        }
      }
    },
    [currentTimestampRef, setPlayerTime, onTogglePlayPause, userWantsRewindPlaying],
  );

  useEffect(() => {
    if (playerRef) {
      playerRef.addEventListener('keydown', handleKeyDown);
      return () => {
        playerRef.removeEventListener('keydown', handleKeyDown);
      };
    }
    return undefined;
  }, [handleKeyDown, playerRef]);

  const skipBackHandler = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      if (currentTimestampRef?.current.value && setPlayerTime) {
        setPlayerTime(
          currentTimestampRef.current.value.getTime() - SKIP_TIME,
          userWantsRewindPlaying,
        );
      }
    },
    [currentTimestampRef, setPlayerTime, userWantsRewindPlaying],
  );

  const skipForwardHandler = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      if (currentTimestampRef?.current.value && setPlayerTime) {
        setPlayerTime(
          currentTimestampRef.current.value.getTime() + SKIP_TIME,
          userWantsRewindPlaying,
        );
      }
    },
    [currentTimestampRef, setPlayerTime, userWantsRewindPlaying],
  );

  return (
    <div
      className={classNames({
        [styles.show]: controlsVisible,
        [styles.camPlayerScrubberControls]: true,
        [styles.hide]: !hasCamRewindPermissions && currentPreviewPlayer === Player.REWIND,
      })}
      data-testid="cam-player-scrubber-controls"
    >
      {playing && (
        <Button
          data-testid="cam-player-pause-button"
          disableRipple
          disableFocusRipple
          disableTouchRipple
          className={styles.playPauseButton}
          onClick={onTogglePlayPause}
          aria-label="Pause Webcam"
        >
          <Pause />
        </Button>
      )}
      {!playing && (
        <Button
          data-testid="cam-player-play-button"
          disableRipple
          disableFocusRipple
          disableTouchRipple
          className={styles.playPauseButton}
          onClick={onTogglePlayPause}
          aria-label="Play Webcam"
        >
          <Play />
        </Button>
      )}
      {hasCurrentTimestamp && currentPreviewPlayer === Player.REWIND && setPlayerTime && (
        <Button
          data-testid="cam-player-rewind-button"
          disableRipple
          disableFocusRipple
          disableTouchRipple
          className={styles.skipButton}
          onClick={skipBackHandler}
          aria-label="Rewind Webcam"
        >
          <SkipBackward />
        </Button>
      )}
      {hasCurrentTimestamp && currentPreviewPlayer === Player.REWIND && setPlayerTime && (
        <Button
          data-testid="cam-player-fast-forward-button"
          disableRipple
          disableFocusRipple
          disableTouchRipple
          className={styles.skipButton}
          onClick={skipForwardHandler}
          aria-label="Fast forward Webcam"
        >
          <SkipForward />
        </Button>
      )}
    </div>
  );
};

export default React.memo(CamPlayerScrubberControls);
